if (!window.Maximize.loadedScript.includes("bulk-order-import.js")) {
  window.Maximize.loadedScript.push("bulk-order-import.js");

  const IMPORT_CONCURRENCY = 6;
  const MAX_ROWS = 100;
  const EXPECTED_COLUMNS = 2; // SKU, Quantity
  const BULK_ORDER_USED_CSV_IMPORT_KEY = "bulk_order_used_csv_import";
  // Duplicated from bulk-order.js's BULK_ORDER_STORAGE_KEY on purpose — see zero-coupling note above.
  const BULK_ORDER_TABLE_STORAGE_KEY = "bulk_order_table_items";

  // Data-only cache shared with bulk-order.js (see bulk-order.js's `_searchCache`) — same
  // object reference, but this file owns its own request/parsing logic. No functions are
  // shared between the two files on purpose, only this cache's data.
  window.Maximize.bulkOrderSearchCache = window.Maximize.bulkOrderSearchCache || {};

  // Runs `worker` over `items` with at most `concurrency` calls in flight at once.
  // Duplicated from bulk-order.js on purpose — see plan notes on zero-coupling between the two files.
  async function runWithConcurrency(items, concurrency, worker) {
    let nextIndex = 0;
    async function processQueue() {
      while (nextIndex < items.length) {
        const currentItem = items[nextIndex++];
        await worker(currentItem);
      }
    }
    const workerCount = Math.min(concurrency, items.length);
    await Promise.all(Array.from({ length: workerCount }, processQueue));
  }

  // Excel/Sheets "CSV UTF-8" exports commonly prepend a BOM to the first cell.
  function stripBOM(text) {
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  }

  // Hand-rolled CSV parser (RFC 4180-ish): handles quoted fields, embedded commas/newlines,
  // and "" escaped quotes. No external library — CSV is plain text, unlike .xlsx/.xls.
  function parseCSV(text) {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += char;
        }
        continue;
      }

      if (char === '"' && field === "") {
        // Only the first character of a field opens a quoted field (RFC 4180);
        // a stray " elsewhere in an unquoted field is treated as a literal character.
        inQuotes = true;
      } else if (char === ",") {
        row.push(field);
        field = "";
      } else if (char === "\n" || char === "\r") {
        if (char === "\r" && text[i + 1] === "\n") i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += char;
      }
    }

    if (field.length > 0 || row.length > 0) {
      row.push(field);
      rows.push(row);
    }

    // Blank rows are NOT filtered out here on purpose — doing so would shift every row
    // after it out of sync with its real line number in the file. Skipping blank rows
    // (without dropping them from the array) is _validateSelectedFile()'s job instead,
    // so physicalRow stays correct for every row reported in an error message.
    return rows;
  }

  // Static reference sample for "Download example file" — shows the expected columns/format
  // only, not a live export of the store's catalog.
  const SAMPLE_CSV_CONTENT = ["SKU (*),Quantity", "00001,70", "00002,60", "00003,30", "00004,30", "00005,50", "00011,50", "00022,60"].join("\r\n");

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xBulkOrderImport", (moneyFormat) => ({
        selectedFile: null,
        isDragOver: false, // dropzone highlight while a file is dragged over it
        isValidating: false, // pre-handle: parsing + validating the file, right after it's selected
        isProcessing: false, // actually resolving + importing, after clicking "Import"
        fileError: "", // single file-level blocking message (wrong type, empty, corrupt, too many products)
        rowErrorLines: [], // already-grouped, ready-to-render strings, e.g. "Row 1, 2: Invalid quantity."
        previewMessage: "", // success message: pre-handle preview only (Phase 1)

        // Phase 2 progress — rendered above the table (this component's root sits right
        // before it in the DOM), no event needed since state + UI live in this component.
        importTotal: 0,
        importProcessed: 0,
        importResultMessage: "", // final success message, e.g. "80/160 have been successfully added."
        importResultErrorLines: [], // Phase 2 errors: no_match / ambiguous_match / search_failed

        _moneyFormat: moneyFormat,
        _strings: {},
        _parsed: null, // { rows } cached after a successful pre-handle validate, reused by submitImport()

        // Whether the table's items were built (at least in part) via a successful CSV
        // import — gates the compact CSV controls bar under the search input (once hasItems)
        // and hides the empty-state "getting started" panel, both rendered inside THIS
        // component's scope so this stays a plain same-scope property (see init()/submitImport()
        // for read/write, and the 'bulk-order:table-cleared' listener below for the reset path
        // — table clearing is handled by xBulkOrder, a separate Alpine component).
        hasUsedCsvImport: false,

        // Confirm popup shown before an import that would replace an already-populated
        // table (see onImportClick()) — skipped when the table is empty, since there is
        // nothing to lose in that case.
        showImportConfirm: false,

        get canImport() {
          return !!this.selectedFile && !this.isValidating && !this.isProcessing && !this.fileError && this.rowErrorLines.length === 0 && !!this._parsed && this._parsed.rows.length > 0;
        },

        get importProgressPercent() {
          return this.importTotal > 0 ? Math.min(100, (this.importProcessed / this.importTotal) * 100) : 0;
        },

        get hasImportProgress() {
          return this.isProcessing || !!this.importResultMessage || this.importResultErrorLines.length > 0;
        },

        init() {
          const stringsEl = this.$el.querySelector("[data-bulk-order-import-strings]");
          this._strings = stringsEl ? JSON.parse(stringsEl.textContent) : { errors: {} };

          try {
            this.hasUsedCsvImport = localStorage.getItem(BULK_ORDER_USED_CSV_IMPORT_KEY) === "true";
          } catch (storageError) {}

          document.addEventListener("bulk-order:table-cleared", () => {
            this.hasUsedCsvImport = false;
            this.importResultMessage = "";
            this.importResultErrorLines = [];
            try {
              localStorage.removeItem(BULK_ORDER_USED_CSV_IMPORT_KEY);
            } catch (storageError) {}
          });

          // xBulkOrder (separate component) dispatches this once an "Add to cart" submit
          // has resolved (full success, partial failure, or a hard failure) — the Phase 2
          // message from a PRIOR import is stale at that point either way.
          document.addEventListener(BULK_ORDER_EVENT.cartSubmitResolved, () => {
            this.importResultMessage = "";
            this.importResultErrorLines = [];
          });
        },

        // ---- File input ----

        onFilePicked(event) {
          const file = event.target.files && event.target.files[0];
          this._handleFile(file);
          event.target.value = "";
        },

        onFileDropped(event) {
          this.isDragOver = false;
          const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
          this._handleFile(file);
        },

        _handleFile(file) {
          this.fileError = "";
          this.rowErrorLines = [];
          this.previewMessage = "";
          this._parsed = null;
          if (!file) return;

          if (!/\.csv$/i.test(file.name || "")) {
            this.fileError = this._strings.errors.invalid_file_type;
            this.selectedFile = null;
            return;
          }

          this.selectedFile = file;
          this._validateSelectedFile();
        },

        // ---- Pre-handle: parse + validate immediately on file select, before Import is enabled ----

        // rawRow: [SKU, Quantity]. `index` is 0-based within the data rows (header already
        // excluded) — physicalRow (index+2) is the line number in the CSV file, used as the
        // row reference for all error reporting.
        normalizeRow(rawRow, index) {
          const physicalRow = index + 2;
          const rowRef = String(physicalRow);

          if (rawRow.length !== EXPECTED_COLUMNS) {
            return { rowRef, columnError: true };
          }

          const [skuRaw, qtyRaw] = rawRow;
          const sku = (skuRaw || "").trim();
          const qtyTrim = (qtyRaw || "").trim();

          let qty = null;
          let qtyError = false;
          if (qtyTrim) {
            // Only positive integers — reject decimals (e.g. "2.5") instead of silently truncating.
            if (!/^\d+$/.test(qtyTrim) || parseInt(qtyTrim, 10) <= 0) qtyError = true;
            else qty = parseInt(qtyTrim, 10);
          }
          // Qty left blank is valid — resolved later from the variant's quantity_rule.min.

          return { rowRef, sku, qty, qtyError, columnError: false };
        },

        // One SKU that shows up on more than one data row → one "Duplicate SKU" line per
        // repeated SKU (not grouped with other error types — the SKU value is part of the
        // message itself, unlike _formatGroupedErrors' generic per-errorKey lines).
        _findDuplicateSkuErrors(rows) {
          const rowRefsBySku = {};
          rows.forEach((row) => {
            const key = row.sku.toLowerCase();
            (rowRefsBySku[key] = rowRefsBySku[key] || { sku: row.sku, rowRefs: [] }).rowRefs.push(row.rowRef);
          });
          return Object.values(rowRefsBySku)
            .filter((entry) => entry.rowRefs.length > 1)
            .map((entry) => {
              const sortedRowRefs = [...entry.rowRefs].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
              const label = (this._strings.row_error_label || "Row [[ROW]]").replace("[[ROW]]", sortedRowRefs.join(", "));
              const message = (this._strings.errors.duplicate_sku || "").replace("[[SKU]]", entry.sku);
              return message + ": " + label;
            });
        },

        // errorsByType: { errorKey: [rowRef, ...] } -> ["<message>: Row 1, 2, 3", ...]
        _formatGroupedErrors(errorsByType) {
          return Object.entries(errorsByType).map(([key, rowRefs]) => {
            // Groups resolved concurrently in submitImport() can push rowRefs out of file
            // order — sort numerically so error lines always read in original row order.
            const sortedRowRefs = [...rowRefs].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const label = (this._strings.row_error_label || "Row [[ROW]]").replace("[[ROW]]", sortedRowRefs.join(", "));
            return (this._strings.errors[key] || key) + ": " + label;
          });
        },

        async _validateSelectedFile() {
          this.isValidating = true;
          this.fileError = "";
          this.rowErrorLines = [];
          this.previewMessage = "";
          this._parsed = null;

          const file = this.selectedFile;

          try {
            const rawText = await file.text();
            const rawRows = parseCSV(stripBOM(rawText));

            if (rawRows.length <= 1) {
              this.fileError = this._strings.errors.empty_file;
              return;
            }

            const normalizedRows = rawRows.slice(1).map((rawRow, index) => this.normalizeRow(rawRow, index));

            const errorsByType = {};
            const validRows = [];
            // Every row with a non-blank SKU, regardless of any other error it has (e.g. a
            // bad quantity) — duplicate-SKU detection needs to see ALL of them, not just the
            // fully-clean ones, otherwise a duplicate is missed whenever one of its rows also
            // happens to have an unrelated error (see _findDuplicateSkuErrors()).
            const rowsWithSku = [];

            // Header row gets the same column-count check as any data row — reported as
            // "Row 1" alongside other invalid_row_format rows, instead of blocking the
            // whole file.
            if (rawRows[0].length !== EXPECTED_COLUMNS) {
              (errorsByType.invalid_row_format = errorsByType.invalid_row_format || []).push("1");
            }

            normalizedRows.forEach((row) => {
              if (row.columnError) {
                (errorsByType.invalid_row_format = errorsByType.invalid_row_format || []).push(row.rowRef);
                return;
              }

              let hasBlockingError = false;

              if (!row.sku) {
                (errorsByType.missing_required_field = errorsByType.missing_required_field || []).push(row.rowRef);
                hasBlockingError = true;
              } else {
                rowsWithSku.push(row);
              }
              if (row.qtyError) {
                (errorsByType.invalid_qty = errorsByType.invalid_qty || []).push(row.rowRef);
                hasBlockingError = true;
              }

              if (!hasBlockingError) validRows.push(row);
            });

            if (Object.keys(errorsByType).length) {
              this.rowErrorLines = this._formatGroupedErrors(errorsByType);
            }

            this.rowErrorLines = this.rowErrorLines.concat(this._findDuplicateSkuErrors(rowsWithSku));

            if (validRows.length > MAX_ROWS) {
              this.fileError = (this._strings.errors.too_many_rows || "").replace("[[COUNT]]", validRows.length);
            }

            if (this.rowErrorLines.length || this.fileError) return;

            this._parsed = { rows: validRows };
            this.previewMessage = (this._strings.preview_message || "").replace("[[ROW_COUNT]]", validRows.length);
          } catch (validateError) {
            console.error("[BulkOrderImport]", validateError);
            this.fileError = this._strings.errors.invalid_file_type;
          } finally {
            this.isValidating = false;
          }
        },

        // ---- Resolve variants via search (SKU only, no handle) ----
        // One search request per row's SKU. Grouping by product title is no longer possible
        // (each row is its own SKU, duplicates are rejected in Phase 1), so the only remaining
        // optimization is the concurrency cap below + the shared SKU cache.

        // Own fetch, deliberately not shared with bulk-order.js's fetchResults() — same
        // Predictive Search + Section Rendering hybrid endpoint, separate implementation.
        // `fields` is restricted to variants.sku only, so matching is scoped to SKU and can't
        // false-positive off a product/variant title that happens to contain the SKU string.
        async fetchCandidatesBySku(sku) {
          const url = Shopify.routes.root + "search/suggest" + "?q=" + encodeURIComponent(sku) + "&resources[type]=product" + "&resources[limit]=10" + "&resources[options][fields]=variants.sku" + "&section_id=bulk-order-search-results";

          const res = await fetch(url);
          if (!res.ok) throw new Error("[BulkOrderImport] search HTTP " + res.status);

          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, "text/html");
          const scriptEl = doc.getElementById("bulk-order-results-data");
          if (!scriptEl) return [];

          return JSON.parse(scriptEl.textContent).map((variantRow) => ({
            ...variantRow,
            variantId: String(variantRow.variantId),
          }));
        },

        async _getCandidatesForSku(sku) {
          const cacheKey = sku.toLowerCase();
          const cached = window.Maximize.bulkOrderSearchCache[cacheKey];
          if (cached) return cached;
          const candidates = await this.fetchCandidatesBySku(sku);
          window.Maximize.bulkOrderSearchCache[cacheKey] = candidates;
          return candidates;
        },

        // Predictive Search can still return partial/prefix SKU matches, so narrow down to
        // the exact SKU (case-insensitive) before accepting a variant.
        _matchRowInCandidates(row, candidates) {
          const skuNorm = row.sku.toLowerCase();
          const matches = candidates.filter((variantRow) => (variantRow.sku || "").trim().toLowerCase() === skuNorm);

          if (!matches.length) return { ok: false, error: "no_match" };
          if (matches.length > 1) return { ok: false, error: "ambiguous_match" };
          return { ok: true, variantRow: matches[0] };
        },

        // Whether the row's quantity is orderable for the matched variant. A blank quantity is
        // fine — buildTableItem resolves it to the rule minimum, which is valid by definition.
        _qtyObeysRule(row, variantRow) {
          if (!row.qty) return true;
          const rule = quantityRuleUtils.normalizeRule(variantRow.min, variantRow.max, variantRow.increment);
          return quantityRuleUtils.isValid(row.qty, rule);
        },

        buildTableItem(row, variantRow) {
          const qty = row.qty || variantRow.quantityMin || 1;
          return {
            ...variantRow,
            priceFormatted: formatMoney(variantRow.price, this._moneyFormat),
            compareAtPriceFormatted: variantRow.compareAtPrice && variantRow.compareAtPrice !== variantRow.price ? formatMoney(variantRow.compareAtPrice, this._moneyFormat) : "",
            priceCents: variantRow.price,
            qty,
          };
        },

        async _resolveRows(rows) {
          const items = [];
          const errors = []; // [{ key, rowRef }]
          let hasNetworkError = false;

          await runWithConcurrency(rows, IMPORT_CONCURRENCY, async (row) => {
            let candidates;
            try {
              candidates = await this._getCandidatesForSku(row.sku);
            } catch (fetchError) {
              if (!navigator.onLine || fetchError instanceof TypeError) {
                hasNetworkError = true;
              } else {
                errors.push({ key: "search_failed", rowRef: row.rowRef });
              }
              this.importProcessed += 1;
              return;
            }

            const result = this._matchRowInCandidates(row, candidates);
            if (!result.ok) {
              errors.push({ key: result.error, rowRef: row.rowRef, sku: row.sku });
            } else {
              // The quantity rule only becomes known once the row is matched to a variant, so
              // this cannot be checked while parsing. A quantity the variant cannot be ordered
              // in is rejected rather than silently adjusted — the table would otherwise hold
              // numbers that don't match the file the merchant uploaded.
              if (!this._qtyObeysRule(row, result.variantRow)) {
                errors.push({ key: "qty_violates_rule", rowRef: row.rowRef });
              } else {
                items.push(this.buildTableItem(row, result.variantRow));
              }
            }
            this.importProcessed += 1;
          });

          if (hasNetworkError) {
            return { items, errorLines: [this._strings.errors.network_error], networkError: true };
          }

          // ambiguous_match needs the SKU spelled out per line ("Multiple items found for SKU
          // X: Row ..."), unlike the other error types, which only group by row — kept out of
          // the generic errorsByType grouping below and formatted separately.
          const ambiguousErrors = errors.filter((error) => error.key === "ambiguous_match");
          const otherErrors = errors.filter((error) => error.key !== "ambiguous_match");

          const errorsByType = {};
          otherErrors.forEach(({ key, rowRef }) => {
            errorsByType[key] = errorsByType[key] || [];
            errorsByType[key].push(rowRef);
          });

          return { items, errorLines: this._formatGroupedErrors(errorsByType).concat(this._formatAmbiguousMatchErrors(ambiguousErrors)) };
        },

        // Groups ambiguous_match errors by SKU (unlike duplicate_sku, a SKU here needs no
        // more than 1 occurrence to be reported — it's already ambiguous in the catalog).
        _formatAmbiguousMatchErrors(errors) {
          const rowRefsBySku = {};
          errors.forEach(({ sku, rowRef }) => {
            const key = sku.toLowerCase();
            (rowRefsBySku[key] = rowRefsBySku[key] || { sku, rowRefs: [] }).rowRefs.push(rowRef);
          });
          return Object.values(rowRefsBySku).map((entry) => {
            const sortedRowRefs = [...entry.rowRefs].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
            const label = (this._strings.row_error_label || "Row [[ROW]]").replace("[[ROW]]", sortedRowRefs.join(", "));
            const message = (this._strings.errors.ambiguous_match || "").replace("[[SKU]]", entry.sku);
            return message + ": " + label;
          });
        },

        // ---- Import (Phase 2 — only runs on click, reuses the pre-handle result) ----

        // Table's current content lives in xBulkOrder's own state, a separate Alpine
        // component (see the hasUsedCsvImport comment above) — read straight from the
        // localStorage key it persists to rather than adding cross-component coupling.
        _tableHasItems() {
          try {
            const raw = localStorage.getItem(BULK_ORDER_TABLE_STORAGE_KEY);
            const savedItems = raw ? JSON.parse(raw) : [];
            return Array.isArray(savedItems) && savedItems.length > 0;
          } catch (storageError) {
            return false;
          }
        },

        // Import replaces the whole table (see submitImport()/bulk-order.js's
        // _replaceImportedItems()) — confirm first if that would destroy existing rows.
        onImportClick() {
          if (!this.canImport) return;
          if (this._tableHasItems()) {
            this.openImportConfirm();
          } else {
            this.submitImport();
          }
        },

        openImportConfirm() {
          this.showImportConfirm = true;
          document.body.classList.add("overflow-y-hidden");
          this.$nextTick(() => {
            Alpine.store("xFocusElement").trapFocus(this.$refs.importConfirmDialog.id, this.$refs.importConfirmCloseBtn.id);
          });
        },

        closeImportConfirm() {
          this.showImportConfirm = false;
          document.body.classList.remove("overflow-y-hidden");
          Alpine.store("xFocusElement").removeTrapFocus();
        },

        confirmImport() {
          this.closeImportConfirm();
          this.submitImport();
        },

        async submitImport() {
          if (!this.canImport) return;

          // Capture what this run needs before resetting shared state below — if the user
          // reopens the popup and picks a new file while this run is still in flight,
          // `this._parsed` gets reassigned but this closure keeps its own reference.
          const rows = this._parsed.rows;
          const total = rows.length;

          this.isProcessing = true;
          this.importTotal = total;
          this.importProcessed = 0;
          this.importResultMessage = "";
          this.importResultErrorLines = [];
          this.rowErrorLines = [];
          this.previewMessage = "";
          this.selectedFile = null;
          this._parsed = null;

          document.dispatchEvent(new CustomEvent(BULK_ORDER_EVENT.importStarted, { bubbles: true }));

          let items = [];
          try {
            const resolved = await this._resolveRows(rows);
            items = resolved.items;
            this.importResultErrorLines = resolved.errorLines;
            if (items.length) {
              this.importResultMessage = (this._strings.success_message || "").replace("[[PROCESSED]]", items.length).replace("[[TOTAL]]", total);
              this.hasUsedCsvImport = true;
              try {
                localStorage.setItem(BULK_ORDER_USED_CSV_IMPORT_KEY, "true");
              } catch (storageError) {}
            }
          } catch (submitError) {
            console.error("[BulkOrderImport]", submitError);
          } finally {
            this.isProcessing = false;
            // Always dispatched, even on failure, so `xBulkOrder` can re-enable "Add to cart"
            // (it listens for this event, not a try/catch outcome).
            document.dispatchEvent(
              new CustomEvent("bulk-order:import-resolved", {
                bubbles: true,
                detail: { items },
              })
            );
          }
        },

        // ---- Export template ----
        // Static sample rows (NOT pulled from the store's real catalog on purpose) — this
        // is a reference for the expected file format/columns only, not a live catalog export.

        triggerFileDownload(filename, content) {
          const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        },

        downloadExampleFile() {
          this.triggerFileDownload("bulk-order-import-template.csv", SAMPLE_CSV_CONTENT);
        },
      }));
    });
  });
}
